import React from "react";
import { HOME } from "@/constants/testIds";
import { FileSearch, Gavel, ShoppingBag, Truck, ClipboardCheck, Navigation, PackageCheck } from "lucide-react";

export const ConnectedWorkspace = () => {
  const flow = [
    { icon: FileSearch,     name: "RFQ™",                  body: "Source verified suppliers" },
    { icon: Gavel,          name: "CommodityBid™",         body: "Run live procurement auctions" },
    { icon: ShoppingBag,    name: "Purchase Order",        body: "Confirm supplier commitment" },
    { icon: Truck,          name: "FreightIQ™",            body: "Book optimised shipping" },
    { icon: ClipboardCheck, name: "Inspection™",           body: "Verify quality before sail" },
    { icon: Navigation,     name: "Live Shipment Tracking™", body: "Watch every milestone", current: true },
    { icon: PackageCheck,   name: "Delivered",             body: "Close the loop" },
  ];

  return (
    <section
      id="workspace"
      data-testid={HOME.workspaceSection}
      className="relative py-24 md:py-32 bg-ink text-[hsl(var(--ivory))] overflow-hidden"
    >
      <div className="absolute inset-0 grain pointer-events-none" />
      <div className="absolute -top-32 right-[-10%] h-[420px] w-[420px] rounded-full bg-[radial-gradient(closest-side,hsla(217,85%,48%,0.22),transparent)]" />

      <div className="container-narrow relative">
        <div className="max-w-2xl mb-14">
          <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-[hsla(40,33%,97%,0.55)] mb-4">
            06 — Connected Workspace
          </div>
          <h2 className="font-display text-4xl lg:text-5xl font-semibold leading-[1.05] text-balance">
            The shipment is never isolated.
          </h2>
          <p className="mt-5 text-[15px] text-[hsla(40,33%,97%,0.72)] leading-relaxed">
            Tracking lives inside one continuous import workflow. Every product in
            the DeMaxtore Workspace feeds the next — context flows end to end.
          </p>
        </div>

        <ol className="relative grid lg:grid-cols-7 gap-3">
          {flow.map((f, i) => {
            const I = f.icon;
            return (
              <li key={f.name} data-testid={HOME.workspaceNode} className="relative">
                {i < flow.length - 1 && (
                  <span className="hidden lg:block absolute top-6 right-[-18px] w-9 text-center text-[hsla(40,33%,97%,0.35)]">↓</span>
                )}
                <div
                  className={`relative rounded-2xl p-5 border h-full transition-all ${
                    f.current
                      ? "glass-dark border-[hsla(217,85%,48%,0.45)]"
                      : "bg-[hsla(40,33%,97%,0.03)] border-[hsla(40,33%,97%,0.10)]"
                  }`}
                >
                  <span
                    className={`grid h-10 w-10 place-items-center rounded-xl mb-3 ${
                      f.current
                        ? "bg-emerald text-white"
                        : "bg-[hsla(40,33%,97%,0.06)] text-[hsla(40,33%,97%,0.85)]"
                    }`}
                  >
                    <I className="h-4 w-4" />
                  </span>
                  <div className="font-display text-[14px] font-semibold leading-snug">
                    {f.name}
                  </div>
                  <p className="mt-1 text-[12px] text-[hsla(40,33%,97%,0.6)] leading-relaxed">
                    {f.body}
                  </p>
                  {f.current && (
                    <span className="absolute top-3 right-3 rounded-full bg-emerald/20 text-[hsl(217,85%,72%)] text-[9.5px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-[hsla(217,85%,55%,0.35)]">
                      live
                    </span>
                  )}
                </div>
              </li>
            );
          })}
        </ol>

        <div className="mt-12 flex flex-wrap items-center justify-between gap-4 rounded-2xl bg-[hsla(40,33%,97%,0.04)] border border-[hsla(40,33%,97%,0.08)] p-5">
          <p className="text-[14px] text-[hsla(40,33%,97%,0.82)] max-w-2xl">
            One Workspace. One import operating system. Tracking is a component —
            not a separate tool, not a separate login, not a separate truth.
          </p>
          <a
            href="#start"
            className="inline-flex items-center gap-2 bg-[hsl(var(--ivory))] !text-[hsl(var(--ink))] px-4 py-2.5 rounded-xl text-[13px] font-medium hover:bg-white transition-colors"
          >
            Explore the Workspace →
          </a>
        </div>
      </div>
    </section>
  );
};
